import { Outlet } from 'react-router-dom';
import { useParams } from 'react-router';
import { useGetUserExternalDevicesQuery, useGetUserAnnotationsQuery, useGetUserNotificationsQuery, useGetUsersQuery } from 'features/api/apiSlice';

// material-ui
import { Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import { ReconciliationOutlined, CommentOutlined, BellOutlined, LineChartOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import AnnotationsTab from 'pages/patients/tabs/AnnotationsTab';
import NotificationsTab from 'pages/patients/tabs/NotificationsTab';
import PatientHealthData from 'pages/patients/COMPASS/tabs/PatientHealthData';
import { useTranslation } from 'react-i18next';
import EmptyUserCard from 'components/cards/skeleton/EmptyUserCard';
import { PatientChartTab } from 'pages/patients/tabs/PatientChartTab';
import { selectCurrentUser } from 'features/auth/authSlice';
import { useAppSelector } from 'app/hooks';
  
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export type CompositionsResponse = FhirEntry[];

export interface Composition {
  resourceType: string;
  id: string;
  meta: {
    versionId: string;
    lastUpdated: string;
    source: string;
  };
  text: {
    status: string;
    div: string;
  };
  status: string;
  type: {
    coding: Coding[];
  };
  subject: Reference[];
  date: string;
  author: Reference[];
  title: string;
  section: Section[];
}

export interface Coding {
  system: string;
  code: string;
  display: string;
}

export interface Reference {
  reference: string;
  display?: string;
}

export interface Section {
  title: string;
  code: {
    coding: Coding[];
  };
  text: {
    status: string;
    div: string;
  };
  orderedBy?: {
    coding: Coding[];
  };
  entry?: Reference[];
}

export class AllQuestionnaireResponse {
  resourceType!: string;
  id!: string;
  meta!: FhirMeta;
  type!: string;
  total!: number;
  link!: FhirLink[];
  entry!: FhirEntry[];
}

export class FhirMeta {
  lastUpdated!: string;
  versionId?: string;
  source?: string;
}

export class FhirLink {
  relation!: string;
  url!: string;
}

export class FhirEntry {
  fullUrl!: string;
  resource!: Composition | QuestionnaireResponse;
  search!: FhirSearch;
}

export class FhirSearch {
  mode!: string;
}

export class QuestionnaireResponse {
  resourceType!: string;
  id!: string;
  meta!: FhirMeta;
  _questionnaire!: FhirQuestionnaireRef;
  status!: string;
  subject!: FhirSubject;
  item!: FhirItem[];
}

export class FhirQuestionnaireRef {
  extension!: FhirExtension[];
}

export class FhirExtension {
  url!: string;
  valueString!: string;
}

export class FhirSubject {
  reference!: string;
  display!: string;
}

export class FhirItem {
  linkId!: string;
  text!: string;
  answer!: FhirAnswer[];
}

export class FhirAnswer {
  valueString?: string;
  valueInteger?: number;
}

async function fetchDiaryVideoResults(patientId: string): Promise<CompositionsResponse> {
  const url = `https://dev-smile.feri.um.si/api/FHIR/compositions/${patientId}?title=MRAST%20Results`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': '007SkyF411M16',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data as CompositionsResponse;
  } catch (error) {
    console.error('Error al hacer la petición:', error);
    throw error;
  }
}

async function fetchQuestionnaireResults(patientId: string): Promise<AllQuestionnaireResponse> {
  const url = `https://dev-smile.feri.um.si/api/FHIR/allQuestionnaireResponse/${patientId}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': '007SkyF411M16',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error al hacer la petición de cuestionarios:', error);
    throw error;
  }
}

// ==============================|| CUSTOMER - CARD PREVIEW ||============================== //
  
export default function PatientFileDialog () {
  const { t } = useTranslation();
  const authUser = useAppSelector(selectCurrentUser);
  const { userid } = useParams();
  
  const { data: users, isLoading: isLoadingUsers, isFetching: isFetchingUsers } = useGetUsersQuery({ parentId: authUser!!.id!!, role: null })

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  
  const { data: notifications, isLoading: isLoadingNotifications, isFetching: isFetchingNotifications } = useGetUserNotificationsQuery({
    userId: userid!!,
    from: null,
    to: moment.utc().endOf("day").toDate().toISOString(),
    readByClinician: null
  })

  const { data: annotations, isLoading: isLoadingAnnotations, isFetching: isFetchingAnnotations } = useGetUserAnnotationsQuery({
    userId: userid!!,
    from: null,
    to: moment.utc().endOf("day").toDate().toISOString()
  })

  const { data: externalDevices, isLoading: isLoadingExternalDevices, isFetching: isFetchingExternalDevices } = useGetUserExternalDevicesQuery(userid!!)

  const [compositionData, setCompositionData] = useState<FhirEntry[]>([]);
  const [isLoadingCompositionData, setIsLoadingCompositionData] = useState(true);
  const [isFetchingCompositionData, setIsFetchingCompositionData] = useState(false);

  useEffect(() => {
    const loadComposition = async () => {
      if (!userid) return;
      try {
        setIsFetchingCompositionData(true);
        const [diaryVideos, questionnaires] = await Promise.all([
          fetchDiaryVideoResults(userid),
          fetchQuestionnaireResults(userid)
        ]);

        const data = [
          ...(Array.isArray(diaryVideos) ? diaryVideos.flat() : []),
          ...(questionnaires?.entry ?? [])
        ];

        setCompositionData(data);
      } catch (err) {
        console.error('Error fetching composition data:', err);
      } finally {
        setIsLoadingCompositionData(false);
        setIsFetchingCompositionData(false);
      }
    };

    loadComposition();
  }, [userid]);

  if (isLoadingAnnotations || isLoadingNotifications || isLoadingUsers || isLoadingExternalDevices) {
    return (
      <Grid container spacing={3}>
          <EmptyUserCard title={t('loading', 'Loading...')} />
      </Grid>
    )
  } else if (isFetchingAnnotations || isFetchingNotifications || isFetchingUsers || isFetchingExternalDevices) {
    return (
      <Grid container spacing={3}>
          <EmptyUserCard title={t('fetching', 'Fetching...')} />
      </Grid>
    )
  } else {
    const patient = users!!.find(x => x.id == userid)

    return (
      <>
        <Box sx={{ width: '100%' }}>
          <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons="auto" aria-label="account profile tab">
            <Tab label={t('health_data', 'Health data')} icon={<ReconciliationOutlined />} iconPosition="start" sx={{textTransform: 'none'}} {...a11yProps(0)}/>
            <Tab label={t('annotations', 'Annotations')} icon={<CommentOutlined />} iconPosition="start" sx={{textTransform: 'none'}} {...a11yProps(1)}/>
            <Tab label={t('notifications', 'Notifications')} icon={<BellOutlined />} iconPosition="start" sx={{textTransform: 'none'}} {...a11yProps(2)}/>
            <Tab label={t('charts', 'Charts')} icon={<LineChartOutlined />} iconPosition="start" sx={{textTransform: 'none'}} {...a11yProps(3)}/>
          </Tabs>
        </Box>
        <Box sx={{ mt: 2.5 }}>
          <CustomTabPanel value={value} index={0}>
            <PatientHealthData compositionData={compositionData} isLoading={isLoadingCompositionData} isFetching={isFetchingCompositionData}/>
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <AnnotationsTab annotations={annotations!!} users={users!!.concat(authUser!!)} isLoading={isLoadingAnnotations} isFetching={isFetchingAnnotations} />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <NotificationsTab notifications={notifications!!} users={[patient!!]} isSingleUser={true} isLoading={isLoadingNotifications} isFetching={isFetchingNotifications} />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={3}>
            <PatientChartTab
              patientId={patient!!.id!!}
              users={users!!.concat(authUser!!)}
              externalDevices={externalDevices!!}
              annotations={annotations ?? []}
            />
          </CustomTabPanel>
          <Box sx={{ mt: 2.5 }}>
            <Outlet />
          </Box>
        </Box>
      </>
    )
  }
}
  